import { useState, type FormEvent } from 'react';
import { PasswordField } from './password-field';

interface NewPasswordFormProps {
  /** Set the permanent password; resolves (and signs in) on success. */
  onSubmit: (newPassword: string) => Promise<void>;
  /** Back to the sign-in card. */
  onCancel: () => void;
}

/**
 * First-login forced reset. Cognito issued a NEW_PASSWORD_REQUIRED challenge —
 * identity is already proven, so we only collect a new password (twice). On
 * success the user is signed in and this card unmounts.
 */
export function NewPasswordForm({ onSubmit, onCancel }: NewPasswordFormProps) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      // Cognito enforces the pool's password policy; surface its message verbatim.
      await onSubmit(password);
      // success → signed in, card unmounts
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not set password.');
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto max-w-sm">
      <div className="terminal-card overflow-hidden">
        <div className="flex items-center gap-3 border-b border-border px-4 py-3">
          <span className="win-dots relative block h-2.5 w-2.5" aria-hidden />
          <span className="ml-8 font-mono text-xs text-muted">admin — set new password</span>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 p-6" noValidate>
          <p className="font-mono text-xs text-muted">
            First sign-in: choose a permanent password.
          </p>

          <PasswordField
            id="new-password"
            label="new password"
            value={password}
            onChange={setPassword}
          />
          <PasswordField
            id="confirm-password"
            label="confirm password"
            value={confirm}
            onChange={setConfirm}
          />

          {error && (
            <p role="alert" className="font-mono text-sm text-red-400">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-md bg-accent px-4 py-2 font-mono text-sm font-medium text-bg transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            {submitting ? 'saving…' : 'set password'}
          </button>

          <div className="text-center">
            <button
              type="button"
              onClick={onCancel}
              className="font-mono text-xs text-muted hover:text-accent"
            >
              back to sign in
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
